/**
 * `ExploreList` — pagina elenco/creazione delle esplorazioni ("Explore").
 *
 * Rotta `/explore` (sezione "Explore", accessibile a ogni utente autenticato:
 * guardia `ProtectedRoute`). Consente di:
 *  - elencare le esplorazioni salvate (`GET /api/explorations`) con nome,
 *    numero di skill cercate e data di creazione;
 *  - creare una nuova esplorazione (`POST /api/explorations`) indicando un nome
 *    e un insieme di skill, ciascuna con un Livello minimo richiesto.
 *
 * Ogni riga rimanda al dettaglio dell'esplorazione (`/explore/:id`), dove si
 * vedono i risultati di matching (persone + candidati). L'autorizzazione
 * autorevole resta l'API: dopo ogni mutazione si invalidano le query pertinenti.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateExplorationRequest,
  ExplorationDto,
  ExplorationSkillDto,
  ExplorationSummaryDto,
  Livello,
  SkillDto,
} from '../shared/dto';
import { useApiClient } from '../api/useApiClient';
import { ExplorationSkillEditor } from '../components/ExplorationSkillEditor';
import {
  Alert,
  Button,
  DataTable,
  Field,
  Icon,
  Loading,
  Modal,
  PageHeader,
  TextInput,
  type Column,
  type SortDirection,
} from '../components/ui';

const explorationsKey = ['explorations'] as const;
const skillsKey = ['skills', 'catalog'] as const;

/** Formatta una data ISO-8601 nel formato locale italiano (data breve). */
function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('it-IT', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Confronto per nome, orientato dalla direzione (per `DataTable`). */
function compareByName(
  a: ExplorationSummaryDto,
  b: ExplorationSummaryDto,
  direction: SortDirection,
): number {
  const sign = direction === 'desc' ? -1 : 1;
  return sign * a.name.localeCompare(b.name, 'it', { sensitivity: 'base' });
}

export default function ExploreList() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [requiredLevels, setRequiredLevels] = useState<Record<string, Livello>>(
    {},
  );
  const [formError, setFormError] = useState<string | null>(null);

  const explorationsQuery = useQuery({
    queryKey: explorationsKey,
    queryFn: () => api.get<ExplorationSummaryDto[]>('/api/explorations'),
  });

  // Catalogo skill per il selettore (caricato solo quando serve, cioè quando il
  // modale di creazione è aperto almeno una volta).
  const skillsQuery = useQuery({
    queryKey: skillsKey,
    queryFn: () => api.get<SkillDto[]>('/api/skills'),
    enabled: isCreateOpen,
  });

  const skillOptions = useMemo(
    () =>
      (skillsQuery.data ?? [])
        .map((s) => ({ id: s.id, name: s.name }))
        .sort((a, b) => a.name.localeCompare(b.name, 'it', { sensitivity: 'base' })),
    [skillsQuery.data],
  );

  const resetForm = () => {
    setName('');
    setSelectedIds([]);
    setRequiredLevels({});
    setFormError(null);
  };

  const createExploration = useMutation({
    mutationFn: (body: CreateExplorationRequest) =>
      api.post<ExplorationDto>('/api/explorations', body),
    onSuccess: (created) => {
      resetForm();
      setIsCreateOpen(false);
      void queryClient.invalidateQueries({ queryKey: explorationsKey });
      // Porta subito l'utente ai risultati della nuova esplorazione.
      navigate(`/explore/${created.id}`);
    },
    onError: () => {
      setFormError('Creazione dell\'esplorazione non riuscita. Riprova.');
    },
  });

  const explorations = useMemo(
    () => explorationsQuery.data ?? [],
    [explorationsQuery.data],
  );

  const trimmedName = name.trim();
  // Tutte le skill selezionate devono avere un Livello richiesto scelto.
  const allLevelsChosen = selectedIds.every(
    (id) => requiredLevels[id] !== undefined,
  );
  const canSubmit =
    trimmedName.length > 0 && selectedIds.length > 0 && allLevelsChosen;

  const columns = useMemo<Column<ExplorationSummaryDto>[]>(
    () => [
      {
        key: 'name',
        header: 'Esplorazione',
        sortable: true,
        sortComparator: compareByName,
        render: (e) => (
          <Link
            to={`/explore/${e.id}`}
            className="coverage-name__link"
            aria-label={`Apri l'esplorazione ${e.name}`}
          >
            {e.name}
            <Icon name="external" size={14} className="coverage-name__link-icon" />
          </Link>
        ),
      },
      {
        key: 'skillCount',
        header: 'Skill cercate',
        sortable: true,
        sortAccessor: (e) => e.skillCount,
        render: (e) => `${e.skillCount}`,
      },
      {
        key: 'createdAt',
        header: 'Creata il',
        sortable: true,
        sortAccessor: (e) => e.createdAt,
        render: (e) => formatDate(e.createdAt),
      },
    ],
    [],
  );

  const buildBody = (): CreateExplorationRequest => {
    const skills: ExplorationSkillDto[] = selectedIds.map((id) => ({
      skillId: id,
      // `canSubmit` garantisce che il Livello sia presente; il `!` è sicuro.
      requiredLevel: requiredLevels[id]!,
    }));
    return { name: trimmedName, skills };
  };

  return (
    <section className="external-cv-list" aria-labelledby="explore-list-title">
      <PageHeader
        title="Explore"
        titleId="explore-list-title"
        subtitle="Crea esplorazioni per trovare persone e candidati con il miglior matching su un set di skill e livelli."
        actions={
          <Button
            type="button"
            variant="primary"
            onClick={() => {
              resetForm();
              setIsCreateOpen(true);
            }}
          >
            Nuova esplorazione
          </Button>
        }
      />

      {explorationsQuery.isLoading ? (
        <Loading>Caricamento esplorazioni…</Loading>
      ) : explorationsQuery.isError ? (
        <Alert tone="error">Impossibile caricare le esplorazioni.</Alert>
      ) : (
        <DataTable
          columns={columns}
          rows={explorations}
          rowKey={(e) => e.id}
          defaultSort={{ key: 'createdAt', direction: 'desc' }}
          emptyMessage="Nessuna esplorazione. Creane una nuova per cercare talenti per skill e livello."
        />
      )}

      {/* Creazione */}
      <Modal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Nuova esplorazione"
        footer={
          <>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsCreateOpen(false)}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              form="explore-create-form"
              variant="primary"
              loading={createExploration.isPending}
              disabled={!canSubmit}
            >
              Crea
            </Button>
          </>
        }
      >
        <form
          id="explore-create-form"
          className="admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!canSubmit) return;
            setFormError(null);
            createExploration.mutate(buildBody());
          }}
        >
          <Field id="explore-name" label="Nome">
            <TextInput
              id="explore-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Es. Sviluppatori backend senior"
            />
          </Field>

          <Field
            id="explore-skills"
            label="Skill cercate"
            hint="Aggiungi le skill da cercare e, per ciascuna, il livello minimo richiesto."
          >
            {skillsQuery.isLoading ? (
              <Loading>Caricamento skill…</Loading>
            ) : skillsQuery.isError ? (
              <Alert tone="error">Impossibile caricare il catalogo skill.</Alert>
            ) : (
              <ExplorationSkillEditor
                id="explore-skills"
                options={skillOptions}
                selectedIds={selectedIds}
                requiredLevels={requiredLevels}
                onChangeIds={setSelectedIds}
                onChangeRequiredLevels={setRequiredLevels}
              />
            )}
          </Field>

          {selectedIds.length > 0 && !allLevelsChosen && (
            <Alert tone="info">
              Scegli un livello richiesto per ogni skill selezionata.
            </Alert>
          )}
          {formError !== null && <Alert tone="error">{formError}</Alert>}
        </form>
      </Modal>
    </section>
  );
}
